import { useCallback, useMemo, useSyncExternalStore } from "react";
import { HR_STATUSES, type HrStatus } from "../types";

export interface EmployeeFilters {
	q?: string;
	departmentId?: string;
	positionId?: string;
	hrStatus?: HrStatus;
	zoneId?: string;
	riskFlagged?: boolean;
	page?: number;
	limit?: number;
}

/**
 * Tartib BARQAROR — URL har renderda almashib turmasligi uchun.
 *
 * ⚠️ Ikki guruhga bo'lingani ATAYLAB: `hrStatus` ular orasida turadi va
 * u `HR_STATUSES` ga qarshi tekshiriladi, ya'ni sodda satr sikliga
 * qo'shib bo'lmaydi. Guruhlar birlashtirilsa serializatsiya tartibi
 * o'zgarib, «kalitlar tartibi barqaror» testi qizarardi.
 */
const TEXT_KEYS = ["q", "departmentId", "positionId"] as const;
const TEXT_KEYS_2 = ["zoneId"] as const;

/**
 * ⚠️ To'plam UCH joyda bir xil: `DataTable` ning `pageSizeOptions` i
 * (`shared/components/DataTable.tsx`), backend'ning `limit.max(100)` i
 * (`employees.schemas.ts`) va `GET /attendance/summary` ·
 * `GET /zones/assignments` marshrutlarining 100 id chegarasi. Birini
 * ko'tarib qolganini qoldirish sahifaning oxirgi qatorlarini jimgina
 * «hududsiz» qilardi.
 */
const LIMITS = [10, 20, 50, 100];
const DEFAULT_PAGE = 1;
const DEFAULT_LIMIT = 20;

const FILTER_EVENT = "skud:employee-filters";

/**
 * ⚠️ `crypto.randomUUID` va boshqa secure-context API'lari BU YERDA
 * ISHLATILMAYDI — staging TLS'siz LAN IP'da ishlaydi va ular
 * `undefined` bo'ladi (`secure-context.test.ts` qorovuli). Bu yerda
 * tasodifiy ID umuman kerak emas.
 */
export function parseEmployeeFilters(search: string): EmployeeFilters {
	const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
	const filters: EmployeeFilters = {};

	for (const key of TEXT_KEYS) {
		const value = params.get(key);

		if (value) {
			filters[key] = value;
		}
	}

	// ⚠️ URL qo'lda tahrirlanadi; noma'lum holat backend'da 422 berib,
	// foydalanuvchi tushuntira olmaydigan xato ko'rsatardi
	// (`useReportFilters.ts` ning `haqiqiySana` qorovuli naqshi).
	const hrStatus = params.get("hrStatus");
	if (hrStatus && (HR_STATUSES as readonly string[]).includes(hrStatus)) {
		filters.hrStatus = hrStatus as HrStatus;
	}

	for (const key of TEXT_KEYS_2) {
		const value = params.get(key);

		if (value) {
			filters[key] = value;
		}
	}

	// ⚠️ FAQAT `"true"`. `"false"` — filtr yo'q degani, `false` EMAS: aks
	// holda serializatsiya → parslash aylanishi `{}` ni
	// `{ riskFlagged: false }` ga aylantirardi (`useLeadFilters.ts` dagi
	// aynan o'sha tuzoq).
	if (params.get("riskFlagged") === "true") {
		filters.riskFlagged = true;
	}

	// ⚠️ `page`/`limit` HAR DOIM qaytariladi (standart bilan) — chaqiruvchi
	// `?? 20` yozishga majbur bo'lmasin; `activeFilterCount` ularni sanamaydi.
	const page = Number(params.get("page"));
	filters.page = Number.isInteger(page) && page >= 1 ? page : DEFAULT_PAGE;

	const limit = Number(params.get("limit"));
	filters.limit = LIMITS.includes(limit) ? limit : DEFAULT_LIMIT;

	return filters;
}

export function serializeEmployeeFilters(filters: EmployeeFilters): string {
	const params = new URLSearchParams();

	for (const key of TEXT_KEYS) {
		const value = filters[key];

		if (value) {
			params.set(key, value);
		}
	}

	if (filters.hrStatus) {
		params.set("hrStatus", filters.hrStatus);
	}

	for (const key of TEXT_KEYS_2) {
		const value = filters[key];

		if (value) {
			params.set(key, value);
		}
	}

	if (filters.riskFlagged === true) {
		params.set("riskFlagged", "true");
	}

	// ⚠️ Standart qiymatlar URL ga YOZILMAYDI — manzil toza qoladi va
	// round-trip testi `{}` ni `{page:1,limit:20}` ga aylantirmaydi.
	if (filters.page !== undefined && filters.page !== DEFAULT_PAGE) {
		params.set("page", String(filters.page));
	}
	if (filters.limit !== undefined && filters.limit !== DEFAULT_LIMIT) {
		params.set("limit", String(filters.limit));
	}

	return params.toString();
}

export function employeesHref(filters: EmployeeFilters): string {
	const qs = serializeEmployeeFilters(filters);

	return qs ? `/employees?${qs}` : "/employees";
}

/**
 * Sarlavha ostidagi «N ta filtr qo'llangan» sanog'i.
 *
 * ⚠️ Sahifalash filtr EMAS — `page`/`limit` sanoqqa kirmaydi, aks holda
 * ikkinchi sahifaga o'tish «2 ta filtr» deb ko'rinardi.
 */
export function activeFilterCount(filters: EmployeeFilters): number {
	let count = 0;

	for (const key of ["q", "departmentId", "positionId", "hrStatus", "zoneId"] as const) {
		if (filters[key]) {
			count += 1;
		}
	}

	if (filters.riskFlagged === true) {
		count += 1;
	}

	return count;
}

/**
 * Filtrni URL ga yozadi va obunachilarni uyg'otadi.
 *
 * ⚠️ **Eksport qilingan sof yon ta'sir** — hookdan tashqarida ham
 * chaqiriladi (F bloki: profil sarlavhasidagi bo'lim/lavozim havolasi
 * `/employees` ga o'tgandan KEYIN filtrni qo'yadi).
 *
 * ⚠️ `replaceState` — `pushState` EMAS: har filtr o'zgarishi tarixga
 * yozilsa «orqaga» tugmasi sahifadan chiqarish o'rniga o'nlab filtr
 * holatidan orqaga surardi (`useLeadFilters.ts` naqshi).
 *
 * ⚠️ Maxsus hodisa MAJBURIY: `history.replaceState()` `popstate`
 * chiqarmaydi, ya'ni usiz komponent qayta render bo'lmasdi.
 */
export function applyEmployeeFilters(next: EmployeeFilters): void {
	const qs = serializeEmployeeFilters(next);
	const url = qs ? `${window.location.pathname}?${qs}` : window.location.pathname;

	window.history.replaceState(null, "", url);
	window.dispatchEvent(new Event(FILTER_EVENT));
}

function subscribe(onChange: () => void): () => void {
	window.addEventListener("popstate", onChange);
	window.addEventListener(FILTER_EVENT, onChange);

	return () => {
		window.removeEventListener("popstate", onChange);
		window.removeEventListener(FILTER_EVENT, onChange);
	};
}

/**
 * Ro'yxat filtrlarining YAGONA manbai — URL.
 *
 * ⚠️ **Nima uchun TanStack Router ning `useSearch()` i emas.**
 * `routeTree.tsx` — poydevor fayli va bloklar unga tegmaydi, ya'ni
 * `/employees` marshrutiga `validateSearch` qo'shib bo'lmaydi; usiz
 * `useSearch()` tiplanmagan `{}` qaytaradi. Naqsh loyihada ikki marta
 * isbotlangan: `modules/leads/hooks/useLeadFilters.ts` va
 * `modules/reports/hooks/useReportFilters.ts`.
 */
export function useEmployeeFilters(): {
	filters: EmployeeFilters;
	setFilters: (next: EmployeeFilters) => void;
} {
	const search = useSyncExternalStore(
		subscribe,
		() => window.location.search,
		// SSR yo'q, lekin `useSyncExternalStore` server snapshot'ini talab
		// qiladi va uni tashlab ketish React ogohlantirishini beradi.
		() => ""
	);

	const filters = useMemo(() => parseEmployeeFilters(search), [search]);
	const setFilters = useCallback((next: EmployeeFilters) => {
		applyEmployeeFilters(next);
	}, []);

	return { filters, setFilters };
}
